import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import apiService from '../services/apiService';
import { Blob } from '../components/blob/Blob';
import { mergeBlobVariantConfig } from '../components/blob/blobVariantConfig';
import {
  isFaceScannerName,
  isProbioticArchitectureName,
  resolveArtefactBlobConfig,
  usesReducedBlobLighting,
} from '../components/blob/resolveArtefactBlobConfig';
import { STUDIO_ENVIRONMENT_HDRI } from '../config/environmentHdri';
import { canonicalPersonaKey } from '../utils/artefactSessionPath';
import { unlockAudioPlayback } from '../utils/audioUnlock';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import LandingKeywordMarquee from '../components/LandingKeywordMarquee';
import OverviewFooterLinks from '../components/OverviewFooterLinks';
import {
  FALLBACK_COLOR,
  buildArtefactColorMap,
  fetchKeywordAnalyticsForMarquee,
  fetchLandingBlobColorAnalytics,
  getArtefactCategoryCounts,
  getGradientFromCounts,
  getTemporalKeywordList,
  getTopKeywordList,
} from '../utils/landingPageAnalytics';
import './IntroPage.css';
import './OverviewPage.css';

/** Match IntroPage landing blobs: lighter mesh than chat/aura. */
const LANDING_BLOB_ICOSAHEDRON_DETAIL = 40;
const PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL = 85;

function formatArtefactDisplayTitle(raw) {
  if (typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function personaCardTitle(personasMap, personaKey) {
  const p = personasMap?.[personaKey];
  const title = typeof p?.display_title === 'string' ? p.display_title.trim() : '';
  const displayName = typeof p?.display_name === 'string' ? p.display_name.trim() : '';
  const base = title || displayName || personaKey;
  return formatArtefactDisplayTitle(base);
}

function landingCardIntroText(persona) {
  if (!persona || typeof persona !== 'object') return '';
  const candidates = [persona.introduction_text, persona.intro];
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const s = raw.trim();
    if (!s || s.startsWith('[STUB]')) continue;
    return s;
  }
  return '';
}

/**
 * Narrow-viewport single-artefact landing for bare /:segment (e.g. /1). Continue calls `onContinue`
 * (typically navigates to /:segment/introduction). No carousel stack or dot nav.
 */
export default function MobileSingleArtefactIntro({ routeSegment, onContinue }) {
  const { t, language } = useLanguage();
  const [personas, setPersonas] = useState({});
  const [personaKeyAliases, setPersonaKeyAliases] = useState({});
  const [keywords, setKeywords] = useState([]);
  const [artefactColor, setArtefactColor] = useState({
    c1: FALLBACK_COLOR,
    c2: FALLBACK_COLOR,
    c3: FALLBACK_COLOR,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const landingKeywordFetchEpochRef = useRef(0);

  const canonicalKey = useMemo(() => {
    if (!routeSegment || Object.keys(personas).length === 0) return null;
    return canonicalPersonaKey(routeSegment, personas, personaKeyAliases);
  }, [routeSegment, personas, personaKeyAliases]);

  const fetchData = useCallback(async () => {
    landingKeywordFetchEpochRef.current += 1;
    const keywordFetchEpoch = landingKeywordFetchEpochRef.current;
    try {
      setError('');
      const [personasResult, keywordAnalyticsData, colorAnalyticsData] = await Promise.all([
        apiService.getPersonas(language),
        fetchKeywordAnalyticsForMarquee(apiService, language),
        fetchLandingBlobColorAnalytics(apiService, language),
      ]);

      if (keywordFetchEpoch !== landingKeywordFetchEpochRef.current) {
        return;
      }

      if (personasResult?.success && personasResult.personas) {
        setPersonas(personasResult.personas);
        const aliases =
          personasResult.persona_key_aliases && typeof personasResult.persona_key_aliases === 'object'
            ? personasResult.persona_key_aliases
            : {};
        setPersonaKeyAliases(aliases);
      }

      const topKeywords = getTopKeywordList(keywordAnalyticsData);
      const temporalKeywords = getTemporalKeywordList(keywordAnalyticsData);
      const pk =
        personasResult?.success && personasResult.personas && routeSegment
          ? canonicalPersonaKey(routeSegment, personasResult.personas, personasResult.persona_key_aliases || {})
          : null;

      if (pk && personasResult?.personas?.[pk]) {
        const artefactCategoryCounts = getArtefactCategoryCounts(
          pk,
          colorAnalyticsData,
          personasResult.personas
        );
        setArtefactColor(getGradientFromCounts(artefactCategoryCounts, pk));
      }

      setKeywords(temporalKeywords.length > 0 ? temporalKeywords : topKeywords);

      if (!personasResult?.success || !personasResult?.personas) {
        setError('Failed to load landing data from backend');
      }
    } catch (e) {
      if (keywordFetchEpoch !== landingKeywordFetchEpochRef.current) {
        return;
      }
      console.error('Mobile artefact intro fetch failed:', e);
      setError('Failed to load landing data from backend');
      setKeywords([]);
    } finally {
      if (keywordFetchEpoch === landingKeywordFetchEpochRef.current) {
        setIsLoading(false);
      }
    }
  }, [language, routeSegment]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const marqueeKeywordRows = useMemo(() => {
    const temporalKeywordStrings = keywords
      .map((item) => item.keyword)
      .filter((keyword) => typeof keyword === 'string' && keyword.length > 0);
    const raw = temporalKeywordStrings;
    if (raw.length === 0) return [];
    const minItems = 12;
    const out = [];
    while (out.length < minItems) {
      out.push(...raw);
    }
    return out;
  }, [keywords]);

  const persona = canonicalKey ? personas[canonicalKey] : null;
  const introText = landingCardIntroText(persona);
  const cardTitle = personaCardTitle(personas, canonicalKey || '');
  const probioticLandingBlob = isProbioticArchitectureName(canonicalKey || routeSegment || cardTitle);
  const faceScannerLandingBlob = isFaceScannerName(canonicalKey || routeSegment || cardTitle);
  const reducedLightingBlob = usesReducedBlobLighting(canonicalKey || routeSegment || cardTitle);
  const config = useMemo(
    () =>
      mergeBlobVariantConfig(resolveArtefactBlobConfig(canonicalKey || routeSegment || ''), 'landing'),
    [canonicalKey, routeSegment]
  );
  const renderedConfig = reducedLightingBlob
    ? { ...config, envMapIntensity: faceScannerLandingBlob ? 0.28 : 0.45, autoRotate: true, autoRotateSpeed: 1.8 }
    : { ...config, autoRotate: true, autoRotateSpeed: 1.8 };

  const handleContinue = useCallback(() => {
    unlockAudioPlayback();
    onContinue();
  }, [onContinue]);

  const layoutClass = 'mobile-layout';

  if (isLoading) {
    return (
      <div className={`App ${layoutClass} overview-app intro-landing`}>
        <header className='App-header overview-page-header'>
          <div className='header-left overview-page-header__brand'>
            <p className='overview-kicker'>{t('app.exhibitionCompanion')}</p>
            <h1 className='overview-main-title'>
              <span className='overview-main-title__link'>{t('app.title')}</span>
            </h1>
          </div>
          <div className='header-right'>
            <LanguageSelector />
          </div>
        </header>
        <main className={`main-content ${layoutClass} overview-main`}>
          <div className='overview-loading overview-loading--center'>{t('introPage.loading')}</div>
        </main>
        <div className='overview-live-themes' aria-hidden>
          <span className='overview-live-themes__label'>{t('app.liveThemes')}</span>
          <div className='overview-live-themes__marquee-wrap'>
            <div className='overview-live-themes__loading'>…</div>
          </div>
          <OverviewFooterLinks />
        </div>
      </div>
    );
  }

  if (!canonicalKey || !persona) {
    return (
      <div className={`App ${layoutClass} overview-app intro-landing`}>
        <header className='App-header overview-page-header'>
          <div className='header-left overview-page-header__brand'>
            <p className='overview-kicker'>{t('app.exhibitionCompanion')}</p>
            <h1 className='overview-main-title'>
              <Link to='/' className='overview-main-title__link' aria-label={t('app.navHomeAria')}>
                {t('app.title')}
              </Link>
            </h1>
          </div>
          <div className='header-right'>
            <LanguageSelector />
          </div>
        </header>
        <main className={`main-content ${layoutClass} overview-main`}>
          <div className='overview-loading overview-loading--center'>{t('app.overviewNoArtefacts')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className={`App ${layoutClass} overview-app intro-landing`}>
      <header className='App-header overview-page-header'>
        <div className='header-left overview-page-header__brand'>
          <p className='overview-kicker'>{t('app.exhibitionCompanion')}</p>
          <h1 className='overview-main-title'>
            <Link to='/' className='overview-main-title__link' aria-label={t('app.navHomeAria')}>
              {t('app.title')}
            </Link>
          </h1>
        </div>
        <div className='header-right'>
          <LanguageSelector />
        </div>
      </header>

      <main className={`main-content ${layoutClass} overview-main`}>
        {error && <div className='overview-error overview-error--banner'>{error}</div>}

        <div className='overview-carousel overview-carousel--single-artefact'>
          <div className='overview-carousel-stack-card-inner'>
            <div className='persona-card overview-exhibition-card clickable'>
              <div className='persona-card-header'>
                <h2 className='persona-name'>{cardTitle}</h2>
              </div>
              <div className='persona-card-body overview-blob-tile-body'>
                <div className='overview-blob-canvas'>
                  <Canvas
                    camera={{ position: [0, 0, 4.5], fov: 54 }}
                    dpr={probioticLandingBlob ? [1, 1.85] : [1, 1.35]}
                    gl={{ antialias: true, alpha: true }}
                  >
                    <ambientLight intensity={faceScannerLandingBlob ? 0.22 : reducedLightingBlob ? 0.34 : 0.6} />
                    <directionalLight position={[5, 5, 5]} intensity={faceScannerLandingBlob ? 0.28 : reducedLightingBlob ? 0.42 : 0.8} />
                    <pointLight position={[-5, -5, 5]} intensity={faceScannerLandingBlob ? 0.12 : reducedLightingBlob ? 0.2 : 0.4} />
                    <Environment files={STUDIO_ENVIRONMENT_HDRI} environmentIntensity={faceScannerLandingBlob ? 0.24 : reducedLightingBlob ? 0.38 : 0.82} />
                    <group scale={[1.42, 1.42, 1.42]}>
                      <Blob
                        gradientColor1={artefactColor.c1}
                        gradientColor2={artefactColor.c2}
                        gradientColor3={artefactColor.c3}
                        config={renderedConfig}
                        icosahedronDetail={
                          probioticLandingBlob
                            ? PROBIOTIC_LANDING_BLOB_ICOSAHEDRON_DETAIL
                            : LANDING_BLOB_ICOSAHEDRON_DETAIL
                        }
                      />
                    </group>
                  </Canvas>
                </div>
              </div>
              {introText ? <p className='persona-intro overview-card-intro'>{introText}</p> : null}
              <div className='persona-card-footer overview-card-footer'>
                <button type='button' className='overview-cta-pill' onClick={handleContinue}>
                  {t('app.tapToTalk')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className='overview-live-themes' aria-label={t('app.liveThemes')}>
        <span className='overview-live-themes__label'>{t('app.liveThemes')}</span>
        <div className='overview-live-themes__marquee-wrap'>
          {marqueeKeywordRows.length > 0 ? (
            <LandingKeywordMarquee keywords={marqueeKeywordRows} />
          ) : (
            <span className='overview-live-themes__empty'>{t('app.liveThemesEmpty')}</span>
          )}
        </div>
        <OverviewFooterLinks />
      </div>
    </div>
  );
}
